import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Download,
  FileDown,
  Gauge,
  Loader2,
  PackageCheck,
  ScanLine,
  Search,
  X
} from "lucide-react";
import type { AssetRecord } from "../services/assets";
import type {
  AssetGroup,
  AssetReservation,
  ExternalService,
  ExternalServiceItem,
  ExternalServiceParticipant,
  ExternalServiceRules
} from "../types/imobi";
import {
  addDays,
  buildOccupancy,
  computeAssetUsage,
  dateKey,
  externalServiceItemTone,
  externalServiceStatusTone,
  formatExternalServiceItemStatus,
  formatExternalServiceStatus,
  formatServicePeriod,
  inclusiveDays,
  initials,
  isActiveReservation,
  parseDateValue,
  reservationPeriod,
  serviceColor,
  serviceSpan,
  startOfDay,
  type OccupancySegment,
  type ServiceSpan
} from "../domain/externalServices";
import { periodsOverlap } from "../domain/reservations";
import { EmptyState, PrimaryButton, SecondaryButton, StatusPill } from "./ui";

const fullDate = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" });

/* ---------------------------------------------------------------- base */

export function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" }) {
  return (
    <span className={`inline-flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-semibold text-brand-800 ${size === "sm" ? "h-7 w-7 text-[11px]" : "h-9 w-9 text-xs"}`}>
      {initials(name)}
    </span>
  );
}

/** Modal que vira "folha" presa embaixo no celular e centraliza a partir do sm. */
export function SheetModal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = "lg",
  zIndex = "z-[95]"
}: {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: "md" | "lg" | "xl";
  zIndex?: string;
}) {
  const width = size === "md" ? "sm:max-w-lg" : size === "lg" ? "sm:max-w-2xl" : "sm:max-w-4xl";
  return (
    <div className={`fixed inset-0 ${zIndex} flex items-end justify-center bg-slate-950/50 backdrop-blur-sm sm:items-center sm:p-4`} role="dialog" aria-modal="true">
      <div className={`flex max-h-[92vh] w-full ${width} flex-col rounded-t-2xl border border-slate-200 bg-white shadow-panel sm:max-h-[88vh] sm:rounded-xl`}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-ink">{title}</h2>
            {subtitle ? <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div> : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar" className="focus-ring inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}

export function ToggleRow({
  title,
  description,
  checked,
  disabled = false,
  onChange
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className={`flex items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-3 ${disabled ? "opacity-70" : "cursor-pointer hover:bg-slate-50"}`}>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{title}</span>
        <span className="mt-0.5 block text-sm text-slate-500">{description}</span>
      </span>
      <span className="relative mt-0.5 inline-flex shrink-0">
        <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
        <span className="h-6 w-11 rounded-full bg-slate-300 transition peer-checked:bg-brand-600 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-400" />
        <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

/* ---------------------------------------------------------------- etapas */

const serviceSteps: Array<{ key: string; label: string; statuses: ExternalService["status"][] }> = [
  { key: "plan", label: "Planejado", statuses: ["rascunho", "aberto"] },
  { key: "pickup", label: "Retirada", statuses: ["aguardando_confirmacao"] },
  { key: "field", label: "Em campo", statuses: ["em_andamento"] },
  { key: "return", label: "Retorno", statuses: ["em_retorno"] },
  { key: "done", label: "Concluído", statuses: ["concluido"] }
];

export function ServiceStatusStepper({ status }: { status: ExternalService["status"] }) {
  if (status === "cancelado") {
    return <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600">Serviço cancelado. As reservas dos bens foram liberadas.</p>;
  }

  const currentIndex = Math.max(0, serviceSteps.findIndex((step) => step.statuses.includes(status)));
  return (
    <ol className="flex items-center gap-1" aria-label="Etapas do serviço">
      {serviceSteps.map((step, index) => {
        const done = index < currentIndex || status === "concluido";
        const current = index === currentIndex && status !== "concluido";
        return (
          <li key={step.key} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <div className="flex w-full items-center">
              <span className={`h-0.5 flex-1 ${index === 0 ? "opacity-0" : done || current ? "bg-brand-600" : "bg-slate-200"}`} />
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${done ? "bg-brand-600 text-white" : current ? "border-2 border-brand-600 bg-white text-brand-700" : "border border-slate-300 bg-white text-slate-400"}`}>
                {done ? <Check size={13} /> : index + 1}
              </span>
              <span className={`h-0.5 flex-1 ${index === serviceSteps.length - 1 ? "opacity-0" : done ? "bg-brand-600" : "bg-slate-200"}`} />
            </div>
            <span className={`truncate text-[11px] font-medium sm:text-xs ${current ? "text-brand-700" : done ? "text-slate-700" : "text-slate-400"}`}>{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/* ---------------------------------------------------------------- calendario */

const weekdayLabels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const monthFormatter = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" });

// "outubro de 2026" -> "Outubro de 2026" (text-transform capitalize deixaria "De" maiusculo)
function monthTitle(date: Date) {
  const label = monthFormatter.format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

type WeekLane = { span: ServiceSpan; startColumn: number; endColumn: number; lane: number };

function layoutWeek(spans: ServiceSpan[], weekStart: Date): WeekLane[] {
  const weekEnd = addDays(weekStart, 6);
  const lanesEnd: number[] = [];
  return spans
    .filter((span) => span.end >= weekStart && span.start <= weekEnd)
    .sort((left, right) => left.start.getTime() - right.start.getTime() || right.end.getTime() - left.end.getTime())
    .map((span) => {
      const startColumn = Math.max(0, Math.round((startOfDay(span.start).getTime() - weekStart.getTime()) / 86400000));
      const endColumn = Math.min(6, Math.round((startOfDay(span.end).getTime() - weekStart.getTime()) / 86400000));
      let lane = lanesEnd.findIndex((end) => end < startColumn);
      if (lane === -1) {
        lane = lanesEnd.length;
        lanesEnd.push(endColumn);
      } else {
        lanesEnd[lane] = endColumn;
      }
      return { span, startColumn, endColumn, lane };
    });
}

export function ExternalServicesCalendar({
  services,
  reservations,
  onSelectService
}: {
  services: ExternalService[];
  reservations: AssetReservation[];
  onSelectService: (serviceId: string) => void;
}) {
  const today = startOfDay(new Date());
  const [monthCursor, setMonthCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(dateKey(today));
  const [showFinished, setShowFinished] = useState(true);
  const maxLanes = 3;

  const spans = useMemo(() => services
    .filter((service) => !service.deletedAt && service.status !== "cancelado" && (showFinished || service.status !== "concluido"))
    .map((service) => serviceSpan(service))
    .filter((span): span is ServiceSpan => Boolean(span)), [services, showFinished]);

  const gridStart = addDays(monthCursor, -monthCursor.getDay());
  const monthEnd = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0);
  const weeksCount = Math.ceil((inclusiveDays(gridStart, monthEnd)) / 7);
  const weeks = Array.from({ length: weeksCount }, (_, index) => addDays(gridStart, index * 7));

  const assetsByService = useMemo(() => {
    const map = new Map<string, Set<string>>();
    reservations.filter((reservation) => reservation.status !== "cancelado").forEach((reservation) => {
      const set = map.get(reservation.serviceId) ?? new Set<string>();
      set.add(reservation.assetId);
      map.set(reservation.serviceId, set);
    });
    return map;
  }, [reservations]);

  const selectedDate = parseDateValue(selectedDay) ?? today;
  const servicesOnSelectedDay = spans.filter((span) => span.start <= selectedDate && span.end >= selectedDate);
  const equipmentOnSelectedDay = useMemo(() => {
    const now = new Date();
    return reservations.filter((reservation) => {
      if (reservation.status === "cancelado") return false;
      const period = reservationPeriod(reservation, now, selectedDate);
      return Boolean(period && startOfDay(period.start) <= selectedDate && startOfDay(period.end) >= selectedDate);
    }).length;
  }, [reservations, selectedDate]);

  function shiftMonth(delta: number) {
    setMonthCursor((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-3 sm:p-4">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => shiftMonth(-1)} aria-label="Mês anterior" className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronLeft size={18} /></button>
          <h2 className="min-w-[9.5rem] text-center text-base font-semibold text-ink">{monthTitle(monthCursor)}</h2>
          <button type="button" onClick={() => shiftMonth(1)} aria-label="Próximo mês" className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronRight size={18} /></button>
          <button
            type="button"
            onClick={() => {
              setMonthCursor(new Date(today.getFullYear(), today.getMonth(), 1));
              setSelectedDay(dateKey(today));
            }}
            className="focus-ring rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Hoje
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showFinished} onChange={(event) => setShowFinished(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
          Mostrar concluídos
        </label>
      </div>

      <div className="p-2 sm:p-3">
        <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {weekdayLabels.map((label) => <div key={label} className="py-1.5">{label}</div>)}
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          {weeks.map((weekStart) => {
            const lanes = layoutWeek(spans, weekStart);
            const visible = lanes.filter((lane) => lane.lane < maxLanes);
            return (
              <div key={dateKey(weekStart)} className="relative grid grid-cols-7 border-b border-slate-200 last:border-b-0">
                {Array.from({ length: 7 }, (_, column) => {
                  const day = addDays(weekStart, column);
                  const key = dateKey(day);
                  const inMonth = day.getMonth() === monthCursor.getMonth();
                  const isToday = key === dateKey(today);
                  const hidden = lanes.filter((lane) => lane.lane >= maxLanes && lane.startColumn <= column && lane.endColumn >= column).length;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedDay(key)}
                      className={`focus-ring relative flex min-h-[5.5rem] flex-col items-start border-r border-slate-200 p-1 text-left last:border-r-0 sm:min-h-[6.75rem] sm:p-1.5 ${inMonth ? "bg-white" : "bg-slate-50"} ${selectedDay === key ? "ring-2 ring-inset ring-brand-400" : "hover:bg-slate-50"}`}
                    >
                      <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-semibold ${isToday ? "bg-brand-700 text-white" : inMonth ? "text-slate-700" : "text-slate-400"}`}>{day.getDate()}</span>
                      {hidden ? <span className="absolute bottom-1 left-1 text-[10px] font-semibold text-slate-500 sm:text-[11px]">+{hidden}</span> : null}
                    </button>
                  );
                })}
                <div className="pointer-events-none absolute inset-x-0 top-7 grid grid-cols-7 gap-y-0.5 sm:top-8 sm:gap-y-1">
                  {visible.map(({ span, startColumn, endColumn, lane }) => {
                    const continuesBefore = span.start < weekStart;
                    const continuesAfter = span.end > addDays(weekStart, 6);
                    return (
                      <button
                        key={span.service.id}
                        type="button"
                        title={`${span.service.title} · ${formatServicePeriod(span.service.startDate, span.service.expectedEndDate)} · ${formatExternalServiceStatus(span.service.status)}`}
                        onClick={() => onSelectService(span.service.id)}
                        className={`pointer-events-auto mx-0.5 truncate px-1 py-0.5 text-left text-[10px] font-semibold leading-tight text-white shadow-sm hover:brightness-110 sm:px-1.5 sm:text-xs ${continuesBefore ? "rounded-l-none" : "rounded-l-md"} ${continuesAfter ? "rounded-r-none" : "rounded-r-md"} ${span.service.status === "concluido" ? "opacity-60" : ""}`}
                        style={{ gridColumn: `${startColumn + 1} / ${endColumn + 2}`, gridRow: lane + 1, backgroundColor: serviceColor(span.service.id) }}
                      >
                        {span.service.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-slate-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-semibold text-ink">{fullDate.format(selectedDate)}</h3>
          <p className="text-sm text-slate-500">{servicesOnSelectedDay.length} serviço(s) · {equipmentOnSelectedDay} equipamento(s) ocupado(s)</p>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {servicesOnSelectedDay.map((span) => (
            <button key={span.service.id} type="button" onClick={() => onSelectService(span.service.id)} className="focus-ring flex min-w-0 items-start gap-3 rounded-lg border border-slate-200 p-3 text-left hover:bg-slate-50">
              <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: serviceColor(span.service.id) }} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-ink">{span.service.title}</span>
                <span className="block text-xs text-slate-500">{formatServicePeriod(span.service.startDate, span.service.expectedEndDate)}</span>
                <span className="mt-1 flex flex-wrap gap-1">
                  <StatusPill tone={externalServiceStatusTone(span.service.status)}>{formatExternalServiceStatus(span.service.status)}</StatusPill>
                  <StatusPill>{assetsByService.get(span.service.id)?.size ?? 0} equip.</StatusPill>
                </span>
              </span>
            </button>
          ))}
        </div>
        {!servicesOnSelectedDay.length ? <p className="mt-2 text-sm text-slate-500">Nenhum serviço neste dia.</p> : null}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- disponibilidade */

type AvailabilityCheck = { state: "livre" } | { state: "ocupado"; reservation: AssetReservation } | { state: "neste_servico" };

function assignLanes(segments: OccupancySegment[]) {
  const lanesEnd: number[] = [];
  return segments.map((segment) => {
    let lane = lanesEnd.findIndex((end) => end < segment.startIndex);
    if (lane === -1) {
      lane = lanesEnd.length;
      lanesEnd.push(segment.endIndex);
    } else {
      lanesEnd[lane] = segment.endIndex;
    }
    return { segment, lane };
  });
}

function segmentStyle(segment: OccupancySegment, color: string) {
  if (segment.reservation.status === "em_uso") {
    return { backgroundColor: color, color: "#fff" };
  }

  if (segment.reservation.status === "concluido") {
    return { backgroundColor: color, color: "#fff", opacity: 0.4 };
  }

  // Reservado (ainda nao saiu): faixa listrada com a cor do servico
  return {
    backgroundImage: `repeating-linear-gradient(135deg, ${color} 0 6px, ${color}cc 6px 12px)`,
    color: "#fff",
    opacity: 0.85
  };
}

export function EquipmentAvailabilityBoard({
  assets,
  reservations,
  services,
  assetGroups,
  onSelectService,
  onOpenAsset
}: {
  assets: AssetRecord[];
  reservations: AssetReservation[];
  services: ExternalService[];
  assetGroups: AssetGroup[];
  onSelectService: (serviceId: string) => void;
  onOpenAsset: (assetId: string) => void;
}) {
  const today = startOfDay(new Date());
  const [windowDays, setWindowDays] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? 7 : 14));
  const [windowStart, setWindowStart] = useState(() => addDays(today, -1));
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("todos");
  const [compareServiceId, setCompareServiceId] = useState("");
  const [checkFrom, setCheckFrom] = useState("");
  const [checkTo, setCheckTo] = useState("");
  const [onlyFree, setOnlyFree] = useState(false);
  const servicesById = useMemo(() => new Map(services.map((service) => [service.id, service])), [services]);
  const comparableServices = services.filter((service) => !service.deletedAt && !["concluido", "cancelado"].includes(service.status));

  const occupancy = useMemo(() => buildOccupancy(reservations, windowStart, windowDays), [reservations, windowStart, windowDays]);
  const days = Array.from({ length: windowDays }, (_, index) => addDays(windowStart, index));
  const checkStart = parseDateValue(checkFrom);
  const checkEnd = parseDateValue(checkTo) ?? checkStart;
  const hasCheck = Boolean(checkStart);

  function availabilityFor(assetId: string): AvailabilityCheck | null {
    if (!checkStart) {
      return null;
    }

    const assetReservations = reservations.filter((reservation) => reservation.assetId === assetId && isActiveReservation(reservation));
    if (compareServiceId && assetReservations.some((reservation) => reservation.serviceId === compareServiceId)) {
      return { state: "neste_servico" };
    }

    const conflict = assetReservations.find((reservation) => reservation.serviceId !== compareServiceId
      && periodsOverlap(checkFrom, checkTo || checkFrom, reservation.plannedStartAt, reservation.expectedReturnAt));
    return conflict ? { state: "ocupado", reservation: conflict } : { state: "livre" };
  }

  const normalizedSearch = search.trim().toLowerCase();
  const rows = assets
    .filter((asset) => groupFilter === "todos" || (groupFilter === "sem_grupo" ? !asset.assetGroup : asset.assetGroup === groupFilter))
    .filter((asset) => !normalizedSearch || [asset.patrimonyNumber, asset.internalCode, asset.description, asset.serialNumber ?? "", asset.brand ?? "", asset.model ?? ""].join(" ").toLowerCase().includes(normalizedSearch))
    .map((asset) => ({ asset, availability: availabilityFor(asset.id) }))
    .filter((row) => !onlyFree || !row.availability || row.availability.state === "livre");
  const freeCount = rows.filter((row) => row.availability?.state === "livre").length;
  const legendServices = Array.from(new Set(Array.from(occupancy.values()).flat().map((segment) => segment.reservation.serviceId)))
    .map((serviceId) => servicesById.get(serviceId))
    .filter((service): service is ExternalService => Boolean(service));

  function applyService(serviceId: string) {
    setCompareServiceId(serviceId);
    const service = servicesById.get(serviceId);
    if (!service) {
      return;
    }

    setCheckFrom(service.startDate.slice(0, 10));
    setCheckTo(service.expectedEndDate ? service.expectedEndDate.slice(0, 10) : "");
    const start = parseDateValue(service.startDate);
    if (start) {
      setWindowStart(addDays(startOfDay(start), -1));
    }
  }

  const dayColumn = "minmax(2.25rem, 1fr)";

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="space-y-3 border-b border-slate-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold text-ink">Disponibilidade dos equipamentos</h2>
            <p className="text-sm text-slate-500">Cada linha é um equipamento; as faixas mostram em qual serviço ele está ou estará.</p>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setWindowStart((current) => addDays(current, -windowDays))} aria-label="Período anterior" className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => setWindowStart(addDays(today, -1))} className="focus-ring rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Hoje</button>
            <button type="button" onClick={() => setWindowStart((current) => addDays(current, windowDays))} aria-label="Próximo período" className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronRight size={18} /></button>
            <select value={windowDays} onChange={(event) => setWindowDays(Number(event.target.value))} className="focus-ring ml-1 rounded-md border border-slate-300 px-2 py-2 text-sm" aria-label="Dias visíveis">
              <option value={7}>7 dias</option>
              <option value={14}>14 dias</option>
              <option value={30}>30 dias</option>
            </select>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input className="focus-ring w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm" placeholder="Buscar equipamento" value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)} aria-label="Grupo de bens">
            <option value="todos">Todos os grupos</option>
            {assetGroups.map((group) => <option key={group.id} value={group.name}>{group.name}</option>)}
            <option value="sem_grupo">Sem grupo</option>
          </select>
          <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm lg:col-span-2" value={compareServiceId} onChange={(event) => (event.target.value ? applyService(event.target.value) : setCompareServiceId(""))} aria-label="Comparar com serviço">
            <option value="">Verificar disponibilidade para um serviço…</option>
            {comparableServices.map((service) => <option key={service.id} value={service.id}>{service.title} · {formatServicePeriod(service.startDate, service.expectedEndDate)}</option>)}
          </select>
        </div>

        <div className="flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-2">
          <label className="text-xs font-medium text-slate-600">De
            <input type="date" className="focus-ring mt-0.5 block rounded-md border border-slate-300 px-2 py-1.5 text-sm" value={checkFrom} onChange={(event) => { setCheckFrom(event.target.value); setCompareServiceId(""); }} />
          </label>
          <label className="text-xs font-medium text-slate-600">Até
            <input type="date" className="focus-ring mt-0.5 block rounded-md border border-slate-300 px-2 py-1.5 text-sm" value={checkTo} min={checkFrom || undefined} onChange={(event) => { setCheckTo(event.target.value); setCompareServiceId(""); }} />
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-600">
            <input type="checkbox" checked={onlyFree} onChange={(event) => setOnlyFree(event.target.checked)} disabled={!hasCheck} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
            Só livres
          </label>
          {hasCheck ? (
            <>
              <span className="pb-2 text-sm font-semibold text-emerald-700">{freeCount} livre(s) no período</span>
              <button type="button" onClick={() => { setCheckFrom(""); setCheckTo(""); setCompareServiceId(""); setOnlyFree(false); }} className="focus-ring mb-1 rounded-md px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100">Limpar</button>
            </>
          ) : <span className="pb-2 text-xs text-slate-500">Escolha um período para ver quem está livre.</span>}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-max" style={{ minWidth: "100%" }}>
          <div className="grid border-b border-slate-200 bg-slate-50 text-center" style={{ gridTemplateColumns: `minmax(9rem, 15rem) repeat(${windowDays}, ${dayColumn})` }}>
            <div className="sticky left-0 z-10 border-r border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Equipamento</div>
            {days.map((day) => {
              const key = dateKey(day);
              const inCheck = checkStart && checkEnd && day >= startOfDay(checkStart) && day <= startOfDay(checkEnd);
              const weekend = day.getDay() === 0 || day.getDay() === 6;
              return (
                <div key={key} className={`border-r border-slate-200 px-0.5 py-1.5 last:border-r-0 ${inCheck ? "bg-brand-100" : weekend ? "bg-slate-100" : ""}`}>
                  <p className="text-[10px] font-semibold uppercase text-slate-500">{weekdayLabels[day.getDay()].slice(0, 3)}</p>
                  <p className={`mx-auto mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${key === dateKey(today) ? "bg-brand-700 text-white" : "text-slate-700"}`}>{day.getDate()}</p>
                </div>
              );
            })}
          </div>

          {rows.map(({ asset, availability }) => {
            const laid = assignLanes(occupancy.get(asset.id) ?? []);
            const laneCount = Math.max(1, ...laid.map((entry) => entry.lane + 1));
            return (
              <div key={asset.id} className="grid border-b border-slate-100 last:border-b-0" style={{ gridTemplateColumns: `minmax(9rem, 15rem) repeat(${windowDays}, ${dayColumn})` }}>
                <button type="button" onClick={() => onOpenAsset(asset.id)} className="focus-ring sticky left-0 z-10 flex min-w-0 items-center gap-2 border-r border-slate-200 bg-white px-2 py-1.5 text-left hover:bg-slate-50" style={{ gridRow: `1 / span ${laneCount}` }}>
                  {asset.mainPhotoUrl ? <img src={asset.mainPhotoUrl} alt="" className="hidden h-8 w-8 shrink-0 rounded object-cover sm:block" /> : null}
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-semibold text-ink">{asset.patrimonyNumber}</span>
                    <span className="block truncate text-[11px] text-slate-500">{asset.description}</span>
                    {asset.status !== "ativo" ? <span className="mt-0.5 inline-block rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800">{asset.status === "em_manutencao" ? "Em manutenção" : "Indisponível"}</span> : null}
                    {availability ? (
                      <span className={`mt-0.5 block truncate text-[10px] font-semibold ${availability.state === "livre" ? "text-emerald-700" : availability.state === "neste_servico" ? "text-brand-700" : "text-rose-700"}`}>
                        {availability.state === "livre" ? "Livre no período" : availability.state === "neste_servico" ? "Já está neste serviço" : `Ocupado: ${availability.reservation.serviceTitle}`}
                      </span>
                    ) : null}
                  </span>
                </button>
                {days.map((day, index) => {
                  const inCheck = checkStart && checkEnd && day >= startOfDay(checkStart) && day <= startOfDay(checkEnd);
                  const weekend = day.getDay() === 0 || day.getDay() === 6;
                  return <div key={index} className={`min-h-[2.75rem] border-r border-slate-100 last:border-r-0 ${inCheck ? "bg-brand-50" : weekend ? "bg-slate-50" : ""}`} style={{ gridColumn: index + 2, gridRow: `1 / span ${laneCount}` }} />;
                })}
                {laid.map(({ segment, lane }) => {
                  const service = servicesById.get(segment.reservation.serviceId);
                  const color = serviceColor(segment.reservation.serviceId);
                  return (
                    <button
                      key={`${segment.reservation.serviceId}-${lane}`}
                      type="button"
                      onClick={() => onSelectService(segment.reservation.serviceId)}
                      title={`${segment.reservation.serviceTitle} · ${segment.reservation.responsibleUserName ?? "sem responsável"} · ${formatServicePeriod(segment.reservation.plannedStartAt, segment.reservation.expectedReturnAt)}`}
                      className="focus-ring z-[1] mx-0.5 my-1.5 self-center truncate rounded-md px-1.5 py-1 text-left text-[11px] font-semibold leading-tight shadow-sm hover:brightness-110"
                      style={{ gridColumn: `${segment.startIndex + 2} / ${segment.endIndex + 3}`, gridRow: lane + 1, ...segmentStyle(segment, color) }}
                    >
                      {service?.title ?? segment.reservation.serviceTitle}
                      {segment.reservation.responsibleUserName ? <span className="font-normal opacity-90"> · {segment.reservation.responsibleUserName}</span> : null}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {!rows.length ? <div className="p-4"><EmptyState title="Nenhum equipamento" description={assets.length ? "Ajuste a busca ou os filtros." : "Marque bens como liberados para serviços externos no cadastro do bem."} /></div> : null}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-200 p-3 text-xs text-slate-600">
        <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded" style={{ backgroundColor: "#0f766e" }} /> Fora com o responsável</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded" style={{ backgroundImage: "repeating-linear-gradient(135deg, #0f766e 0 4px, #0f766ecc 4px 8px)", opacity: 0.85 }} /> Reservado</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-5 rounded opacity-40" style={{ backgroundColor: "#0f766e" }} /> Já devolvido</span>
        {legendServices.slice(0, 8).map((service) => (
          <button key={service.id} type="button" onClick={() => onSelectService(service.id)} className="flex items-center gap-1.5 hover:underline">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: serviceColor(service.id) }} /> {service.title}
          </button>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- uso dos equipamentos */

const percent = new Intl.NumberFormat("pt-BR", { style: "percent", maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });

export function EquipmentUsagePanel({
  assets,
  reservations,
  onOpenAsset
}: {
  assets: AssetRecord[];
  reservations: AssetReservation[];
  onOpenAsset: (assetId: string) => void;
}) {
  const [windowDays, setWindowDays] = useState(90);
  const [sortBy, setSortBy] = useState<"utilization" | "services" | "days">("utilization");
  const [search, setSearch] = useState("");
  const usage = useMemo(() => computeAssetUsage(reservations, new Date(), windowDays), [reservations, windowDays]);

  const rows = assets
    .map((asset) => ({ asset, metrics: usage.get(asset.id) }))
    .filter((row) => {
      const normalized = search.trim().toLowerCase();
      return !normalized || [row.asset.patrimonyNumber, row.asset.description, row.asset.internalCode].join(" ").toLowerCase().includes(normalized);
    })
    .sort((left, right) => {
      const a = left.metrics;
      const b = right.metrics;
      if (sortBy === "services") return (b?.servicesCount ?? 0) - (a?.servicesCount ?? 0);
      if (sortBy === "days") return (b?.totalDaysOut ?? 0) - (a?.totalDaysOut ?? 0);
      return (b?.utilizationInWindow ?? 0) - (a?.utilizationInWindow ?? 0);
    });

  const used = rows.filter((row) => (row.metrics?.servicesCount ?? 0) > 0);
  const averageUtilization = rows.length ? rows.reduce((sum, row) => sum + (row.metrics?.utilizationInWindow ?? 0), 0) / rows.length : 0;
  const totalServices = used.reduce((sum, row) => sum + (row.metrics?.servicesCount ?? 0), 0);
  const totalDays = used.reduce((sum, row) => sum + (row.metrics?.totalDaysOut ?? 0), 0);
  const idle = rows.filter((row) => (row.metrics?.daysOutInWindow ?? 0) === 0).length;

  function exportCsv() {
    const header = ["Patrimonio", "Descricao", "Saidas", "Dias fora (total)", "Media de dias por saida", `Dias fora (${windowDays}d)`, `Ocupacao (${windowDays}d)`, "Ultimo uso", "Reservas futuras"];
    const lines = rows.map(({ asset, metrics }) => [
      asset.patrimonyNumber,
      asset.description,
      String(metrics?.servicesCount ?? 0),
      String(metrics?.totalDaysOut ?? 0),
      decimal.format(metrics?.averageDaysPerService ?? 0),
      String(metrics?.daysOutInWindow ?? 0),
      percent.format(metrics?.utilizationInWindow ?? 0),
      metrics?.lastUseEnd ? fullDate.format(metrics.lastUseEnd) : "",
      String(metrics?.upcomingReservations ?? 0)
    ]);
    const csv = [header, ...lines].map((line) => line.map((value) => `"${value.replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `uso-equipamentos-${dateKey(new Date())}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <UsageStat label={`Ocupação média (${windowDays}d)`} value={percent.format(averageUtilization)} />
        <UsageStat label="Saídas registradas" value={String(totalServices)} />
        <UsageStat label="Média de dias por saída" value={totalServices ? decimal.format(totalDays / totalServices) : "-"} />
        <UsageStat label={`Parados nos últimos ${windowDays}d`} value={String(idle)} tone={idle ? "amber" : undefined} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-3 sm:p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-ink"><Gauge size={18} className="text-brand-700" /> Uso dos equipamentos</h2>
            <p className="text-sm text-slate-500">Base para planejar manutenção preventiva e revisar a depreciação de quem roda muito (ou está parado).</p>
          </div>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 sm:flex sm:flex-wrap">
            <label className="relative col-span-3 block sm:w-56">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input className="focus-ring w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm" placeholder="Buscar" value={search} onChange={(event) => setSearch(event.target.value)} />
            </label>
            <select className="focus-ring rounded-md border border-slate-300 px-2 py-2 text-sm" value={windowDays} onChange={(event) => setWindowDays(Number(event.target.value))} aria-label="Período">
              <option value={30}>30 dias</option>
              <option value={90}>90 dias</option>
              <option value={180}>180 dias</option>
              <option value={365}>12 meses</option>
            </select>
            <select className="focus-ring rounded-md border border-slate-300 px-2 py-2 text-sm" value={sortBy} onChange={(event) => setSortBy(event.target.value as typeof sortBy)} aria-label="Ordenar">
              <option value="utilization">Mais ocupados</option>
              <option value="services">Mais saídas</option>
              <option value="days">Mais dias fora</option>
            </select>
            <SecondaryButton onClick={exportCsv} disabled={!rows.length}><Download size={16} /> CSV</SecondaryButton>
          </div>
        </div>
        <ul className="divide-y divide-slate-100">
          {rows.map(({ asset, metrics }) => {
            const utilization = metrics?.utilizationInWindow ?? 0;
            return (
              <li key={asset.id}>
                <button type="button" onClick={() => onOpenAsset(asset.id)} className="focus-ring grid w-full gap-2 px-3 py-3 text-left hover:bg-slate-50 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_repeat(3,minmax(0,0.6fr))] sm:items-center sm:px-4">
                  <span className="flex min-w-0 items-center gap-3">
                    {asset.mainPhotoUrl ? <img src={asset.mainPhotoUrl} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" /> : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400"><PackageCheck size={18} /></span>}
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-ink">{asset.patrimonyNumber}</span>
                      <span className="block truncate text-xs text-slate-500">{asset.description}</span>
                    </span>
                  </span>
                  <span className="block">
                    <span className="flex items-center justify-between text-xs text-slate-500"><span>Ocupação {windowDays}d</span><span className="font-semibold text-slate-700">{percent.format(utilization)}</span></span>
                    <span className="mt-1 block h-2 overflow-hidden rounded-full bg-slate-100">
                      <span className={`block h-full rounded-full ${utilization >= 0.7 ? "bg-rose-700" : utilization >= 0.35 ? "bg-brand-600" : "bg-slate-400"}`} style={{ width: `${Math.min(100, Math.round(utilization * 100))}%` }} />
                    </span>
                  </span>
                  <span className="grid grid-cols-3 gap-2 text-xs sm:contents">
                    <UsageCell label="Saídas" value={String(metrics?.servicesCount ?? 0)} />
                    <UsageCell label="Dias fora" value={String(metrics?.totalDaysOut ?? 0)} hint={metrics?.servicesCount ? `${decimal.format(metrics.averageDaysPerService)} por saída` : undefined} />
                    <UsageCell label="Último uso" value={metrics?.currentlyOut ? "Fora agora" : metrics?.lastUseEnd ? fullDate.format(metrics.lastUseEnd) : "Nunca"} hint={metrics?.upcomingReservations ? `${metrics.upcomingReservations} reserva(s) futura(s)` : undefined} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {!rows.length ? <div className="p-4"><EmptyState title="Sem equipamentos" description="Os bens liberados para serviços externos aparecem aqui com o histórico de uso." /></div> : null}
      </div>
    </section>
  );
}

function UsageStat({ label, value, tone }: { label: string; value: string; tone?: "amber" }) {
  return (
    <div className={`rounded-xl border p-3 shadow-sm sm:p-4 ${tone === "amber" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-xl font-semibold sm:text-2xl ${tone === "amber" ? "text-amber-800" : "text-ink"}`}>{value}</p>
    </div>
  );
}

function UsageCell({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <span className="block min-w-0">
      <span className="block text-[11px] text-slate-500">{label}</span>
      <span className="block truncate text-sm font-semibold text-ink">{value}</span>
      {hint ? <span className="block truncate text-[11px] text-slate-500">{hint}</span> : null}
    </span>
  );
}

/* ---------------------------------------------------------------- regras do gerente */

export function ExternalServiceRulesPanel({
  rules,
  canEdit,
  onSave
}: {
  rules: ExternalServiceRules;
  canEdit: boolean;
  onSave: (rules: ExternalServiceRules) => Promise<void>;
}) {
  const [draft, setDraft] = useState(rules);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const changed = JSON.stringify(draft) !== JSON.stringify(rules);

  useEffect(() => setDraft(rules), [rules]);

  function update<K extends keyof ExternalServiceRules>(key: K, value: ExternalServiceRules[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
    setMessage("");
  }

  async function handleSave() {
    setIsSaving(true);
    try {
      await onSave(draft);
      setMessage("Regras salvas. Valem para os próximos serviços e conferências.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar as regras.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="space-y-4">
      {!canEdit ? <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">Somente quem gerencia as configurações da empresa pode alterar estas regras.</p> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <h2 className="font-semibold text-ink">Reservas e participantes</h2>
        <p className="text-sm text-slate-500">Padrões aplicados a cada novo serviço. O gerente ainda pode mudar serviço a serviço.</p>
        <div className="mt-3 grid gap-2">
          <ToggleRow title="Participantes podem reservar equipamentos" description="Quem o gerente colocou no serviço pode reservar bens para si (só dos grupos que tem acesso). A saída continua dependendo da conferência." checked={draft.participantsCanReserveByDefault} disabled={!canEdit} onChange={(value) => update("participantsCanReserveByDefault", value)} />
          <ToggleRow title="Entrada livre de participantes" description="Qualquer usuário da empresa com acesso ao módulo pode entrar no serviço sozinho." checked={draft.selfEnrollmentByDefault} disabled={!canEdit} onChange={(value) => update("selfEnrollmentByDefault", value)} />
          <ToggleRow title="Transferência em campo" description="Quem está com um bem pode passá-lo para outro participante sem depender do gerente." checked={draft.peerTransferByDefault} disabled={!canEdit} onChange={(value) => update("peerTransferByDefault", value)} />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <h2 className="font-semibold text-ink">Retirada</h2>
        <div className="mt-3 grid gap-2">
          <ToggleRow title="Exigir conferência do gerente na saída" description="O bem só sai depois que alguém com permissão de liberar confere e registra as observações (ex.: câmera sem alça). Desligado, a confirmação do responsável já libera." checked={draft.requirePickupConference} disabled={!canEdit} onChange={(value) => update("requirePickupConference", value)} />
          <ToggleRow title="POP de retirada" description="Executa o checklist de retirada do equipamento antes de liberar. Desligado por padrão." checked={draft.pickupPopEnabled} disabled={!canEdit} onChange={(value) => update("pickupPopEnabled", value)} />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <h2 className="font-semibold text-ink">Retorno</h2>
        <div className="mt-3 grid gap-2">
          <ToggleRow title="POP de retorno" description="Lista de tarefas de teste e registro de funcionamento na devolução, conforme os POPs cadastrados para o bem, tipo ou grupo." checked={draft.returnPopEnabled} disabled={!canEdit} onChange={(value) => update("returnPopEnabled", value)} />
          <label className={`block rounded-lg border border-slate-200 bg-white p-3 ${!draft.returnPopEnabled ? "opacity-60" : ""}`}>
            <span className="block text-sm font-semibold text-ink">Quem executa o POP de retorno</span>
            <select className="focus-ring mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={draft.returnPopPerformer} disabled={!canEdit || !draft.returnPopEnabled} onChange={(event) => update("returnPopPerformer", event.target.value as ExternalServiceRules["returnPopPerformer"])}>
              <option value="responsavel">O responsável, ao devolver o bem</option>
              <option value="conferente">Quem confere o retorno</option>
            </select>
            <span className="mt-1 block text-xs text-slate-500">Pelo responsável, o POP fica atribuído a quem estava com o bem. Se ele não preencher, o conferente executa ao aprovar o retorno.</span>
          </label>
        </div>
      </div>

      {message ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
      {canEdit ? (
        <div className="flex justify-end gap-2">
          <SecondaryButton onClick={() => setDraft(rules)} disabled={!changed || isSaving}>Descartar</SecondaryButton>
          <PrimaryButton onClick={() => void handleSave()} disabled={!changed || isSaving}>{isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Salvar regras</PrimaryButton>
        </div>
      ) : null}
    </section>
  );
}

/* ---------------------------------------------------------------- conferencia de saida */

const quickExitNotes = ["Sem alça", "Sem case/bolsa", "Sem carregador", "Sem cartão de memória", "Riscos aparentes", "Bateria fraca"];

export type PickupConferenceEntry = { item: ExternalServiceItem; exitNotes: string };

export function PickupConferenceModal({
  service,
  items,
  participants,
  popRequiredFor,
  popDone,
  onRunPop,
  onRelease,
  onClose
}: {
  service: ExternalService;
  items: ExternalServiceItem[];
  participants: ExternalServiceParticipant[];
  popRequiredFor: (item: ExternalServiceItem) => boolean;
  popDone: Record<string, string>;
  onRunPop: (item: ExternalServiceItem) => void;
  onRelease: (entries: PickupConferenceEntry[]) => Promise<void>;
  onClose: () => void;
}) {
  const [participantFilter, setParticipantFilter] = useState(() => {
    const holders = Array.from(new Set(items.map((item) => item.assignedToUserId).filter(Boolean)));
    return holders.length === 1 ? holders[0] ?? "todos" : "todos";
  });
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, string>>(() => Object.fromEntries(items.map((item) => [item.id, item.exitNotes ?? ""])));
  const [scanValue, setScanValue] = useState("");
  const [scanMessage, setScanMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const visibleItems = items.filter((item) => participantFilter === "todos" || item.assignedToUserId === participantFilter);
  const isBlocked = (item: ExternalServiceItem) => popRequiredFor(item) && !popDone[item.id] && !item.pickupInspectionId;
  const selected = visibleItems.filter((item) => checked[item.id] && !isBlocked(item));
  const holders = participants.filter((participant) => items.some((item) => item.assignedToUserId === participant.userId));

  function toggle(item: ExternalServiceItem, value: boolean) {
    setChecked((current) => ({ ...current, [item.id]: value }));
  }

  function appendNote(itemId: string, note: string) {
    setNotes((current) => {
      const existing = current[itemId]?.trim() ?? "";
      if (existing.toLowerCase().includes(note.toLowerCase())) {
        return current;
      }
      return { ...current, [itemId]: existing ? `${existing}; ${note}` : note };
    });
  }

  // Leitor de codigo de barras USB "digita" o codigo e da Enter: marca o item conferido
  function handleScan() {
    const value = scanValue.trim().toLowerCase();
    if (!value) {
      return;
    }

    const match = items.find((item) => [item.patrimonyNumber, item.internalCode ?? "", item.serialNumber ?? "", item.assetId].some((code) => code && code.toLowerCase() === value));
    if (!match) {
      setScanMessage(`Nenhum bem deste serviço com o código "${scanValue.trim()}".`);
    } else if (isBlocked(match)) {
      setScanMessage(`${match.patrimonyNumber}: execute o POP de retirada antes de liberar.`);
    } else {
      toggle(match, true);
      setScanMessage(`${match.patrimonyNumber} conferido.`);
    }
    setScanValue("");
  }

  async function handleRelease() {
    setIsSaving(true);
    setError("");
    try {
      await onRelease(selected.map((item) => ({ item, exitNotes: notes[item.id] ?? "" })));
    } catch (releaseError) {
      setError(releaseError instanceof Error ? releaseError.message : "Não foi possível liberar os bens.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <SheetModal
      title="Conferência de retirada"
      subtitle={<>{service.title} · confira cada bem com o responsável e registre qualquer detalhe antes de liberar.</>}
      onClose={onClose}
      size="xl"
      zIndex="z-[90]"
      footer={(
        <>
          <span className="mr-auto self-center text-sm text-slate-500">{selected.length} de {visibleItems.length} conferido(s)</span>
          <SecondaryButton onClick={onClose} disabled={isSaving}>Fechar</SecondaryButton>
          <PrimaryButton onClick={() => void handleRelease()} disabled={!selected.length || isSaving}>{isSaving ? <Loader2 size={16} className="animate-spin" /> : <PackageCheck size={16} />} Liberar {selected.length || ""} bem(ns)</PrimaryButton>
        </>
      )}
    >
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={participantFilter} onChange={(event) => setParticipantFilter(event.target.value)} aria-label="Responsável">
          <option value="todos">Todos os responsáveis ({items.length})</option>
          {holders.map((participant) => <option key={participant.userId} value={participant.userId}>{participant.userName} ({items.filter((item) => item.assignedToUserId === participant.userId).length})</option>)}
        </select>
        <label className="relative block">
          <ScanLine className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            className="focus-ring w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm"
            placeholder="Bipar ou digitar patrimônio + Enter"
            value={scanValue}
            onChange={(event) => setScanValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleScan();
              }
            }}
          />
        </label>
        <SecondaryButton onClick={() => setChecked((current) => ({ ...current, ...Object.fromEntries(visibleItems.filter((item) => !isBlocked(item)).map((item) => [item.id, true])) }))} disabled={!visibleItems.length}>
          <Check size={16} /> Marcar todos
        </SecondaryButton>
      </div>
      {scanMessage ? <p className="mt-2 text-sm text-slate-600">{scanMessage}</p> : null}

      <ul className="mt-3 space-y-2">
        {visibleItems.map((item) => {
          const blocked = isBlocked(item);
          const isChecked = Boolean(checked[item.id]) && !blocked;
          return (
            <li key={item.id} className={`rounded-lg border p-3 ${isChecked ? "border-brand-300 bg-brand-50" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start gap-3">
                <label className="mt-1 flex shrink-0 cursor-pointer items-center">
                  <input type="checkbox" className="h-5 w-5 rounded border-slate-300 text-brand-700" checked={isChecked} disabled={blocked} onChange={(event) => toggle(item, event.target.checked)} aria-label={`Conferido ${item.patrimonyNumber}`} />
                </label>
                {item.mainPhotoUrl ? <img src={item.mainPhotoUrl} alt="" className="h-14 w-14 shrink-0 rounded-md object-cover" /> : null}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-ink">{item.patrimonyNumber}</p>
                    <StatusPill tone={externalServiceItemTone(item.status)}>{formatExternalServiceItemStatus(item.status)}</StatusPill>
                  </div>
                  <p className="truncate text-sm text-slate-600">{item.description}</p>
                  <p className="text-xs text-slate-500">{item.assignedToUserName ?? "Sem responsável"}{item.serialNumber ? ` · Série ${item.serialNumber}` : ""}</p>
                </div>
              </div>
              <div className="mt-2 sm:pl-8">
                <input
                  className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Observação de saída (opcional)"
                  value={notes[item.id] ?? ""}
                  onChange={(event) => setNotes((current) => ({ ...current, [item.id]: event.target.value }))}
                />
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {quickExitNotes.map((note) => (
                    <button key={note} type="button" onClick={() => appendNote(item.id, note)} className="focus-ring rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-100">+ {note}</button>
                  ))}
                </div>
                {popRequiredFor(item) ? (
                  <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md p-2 text-xs ${blocked ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}>
                    <span className="flex items-center gap-1.5 font-semibold">{blocked ? <AlertTriangle size={14} /> : <Check size={14} />} {blocked ? "POP de retirada pendente" : "POP de retirada registrado"}</span>
                    {blocked ? <SecondaryButton onClick={() => onRunPop(item)}><ClipboardCheck size={16} /> Executar POP</SecondaryButton> : null}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {!visibleItems.length ? <EmptyState title="Nada para conferir" description="Não há bens aguardando retirada para este filtro." /> : null}
      {error ? <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</p> : null}
    </SheetModal>
  );
}

/* ---------------------------------------------------------------- extrato em PDF */

export function ExternalServicePdfModal({
  participants,
  items,
  initialParticipantIds = [],
  isGenerating,
  onGenerate,
  onClose
}: {
  participants: ExternalServiceParticipant[];
  items: ExternalServiceItem[];
  initialParticipantIds?: string[];
  isGenerating: boolean;
  onGenerate: (participantIds: string[], includeSignatures: boolean) => void;
  onClose: () => void;
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialParticipantIds);
  const [includeSignatures, setIncludeSignatures] = useState(true);
  const allSelected = !selectedIds.length;

  return (
    <SheetModal
      title="Extrato do serviço"
      subtitle="PDF com participantes e equipamentos, pronto para imprimir e validar em campo."
      onClose={onClose}
      size="md"
      footer={(
        <>
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => onGenerate(selectedIds, includeSignatures)} disabled={isGenerating}>{isGenerating ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} Gerar PDF</PrimaryButton>
        </>
      )}
    >
      <p className="text-sm font-medium text-slate-700">Participantes</p>
      <div className="mt-2 space-y-1.5">
        <label className={`flex items-center gap-3 rounded-lg border p-2.5 text-sm ${allSelected ? "border-brand-300 bg-brand-50" : "border-slate-200"}`}>
          <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-700" checked={allSelected} onChange={() => setSelectedIds([])} />
          <span className="font-semibold text-ink">Todos</span>
          <span className="ml-auto text-xs text-slate-500">{items.length} bem(ns)</span>
        </label>
        {participants.map((participant) => {
          const count = items.filter((item) => item.assignedToUserId === participant.userId).length;
          const isSelected = selectedIds.includes(participant.userId);
          return (
            <label key={participant.userId} className={`flex items-center gap-3 rounded-lg border p-2.5 text-sm ${isSelected ? "border-brand-300 bg-brand-50" : "border-slate-200"}`}>
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand-700"
                checked={isSelected}
                onChange={(event) => setSelectedIds((current) => event.target.checked ? [...current, participant.userId] : current.filter((id) => id !== participant.userId))}
              />
              <Avatar name={participant.userName} size="sm" />
              <span className="min-w-0 truncate text-ink">{participant.userName}</span>
              <span className="ml-auto shrink-0 text-xs text-slate-500">{count} bem(ns)</span>
            </label>
          );
        })}
      </div>
      <div className="mt-4">
        <ToggleRow title="Campos de assinatura" description="Declaração de recebimento e linhas para o responsável e os conferentes de saída e retorno." checked={includeSignatures} onChange={setIncludeSignatures} />
      </div>
    </SheetModal>
  );
}

/* ---------------------------------------------------------------- meus equipamentos */

export function MyEquipmentPanel({
  reservations,
  currentUserId,
  assets,
  services,
  onSelectService
}: {
  reservations: AssetReservation[];
  currentUserId: string;
  assets: AssetRecord[];
  services: ExternalService[];
  onSelectService: (serviceId: string) => void;
}) {
  const assetsById = useMemo(() => new Map(assets.map((asset) => [asset.id, asset])), [assets]);
  const servicesById = useMemo(() => new Map(services.map((service) => [service.id, service])), [services]);
  const mine = reservations.filter((reservation) => reservation.responsibleUserId === currentUserId && isActiveReservation(reservation));
  const byService = Array.from(mine.reduce((map, reservation) => {
    const list = map.get(reservation.serviceId) ?? [];
    list.push(reservation);
    map.set(reservation.serviceId, list);
    return map;
  }, new Map<string, AssetReservation[]>()).entries())
    .sort(([, left], [, right]) => left[0].plannedStartAt.localeCompare(right[0].plannedStartAt));
  const now = new Date();

  if (!byService.length) {
    return <EmptyState title="Nenhum equipamento com você" description="Quando um bem for reservado ou entregue para você em um serviço, ele aparece aqui." />;
  }

  return (
    <section className="space-y-3">
      {byService.map(([serviceId, list]) => {
        const service = servicesById.get(serviceId);
        return (
          <div key={serviceId} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <button type="button" onClick={() => onSelectService(serviceId)} className="focus-ring flex w-full items-start gap-3 border-b border-slate-200 p-3 text-left hover:bg-slate-50 sm:p-4">
              <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: serviceColor(serviceId) }} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-ink">{service?.title ?? list[0].serviceTitle}</span>
                <span className="block text-xs text-slate-500">{formatServicePeriod(list[0].plannedStartAt, list[0].expectedReturnAt)}</span>
              </span>
              {service ? <StatusPill tone={externalServiceStatusTone(service.status)}>{formatExternalServiceStatus(service.status)}</StatusPill> : null}
            </button>
            <ul className="divide-y divide-slate-100">
              {list.map((reservation) => {
                const asset = assetsById.get(reservation.assetId);
                const expected = parseDateValue(reservation.expectedReturnAt);
                const overdue = reservation.status === "em_uso" && expected && startOfDay(expected) < startOfDay(now);
                return (
                  <li key={`${reservation.serviceId}-${reservation.assetId}`} className="flex items-center gap-3 px-3 py-2.5 sm:px-4">
                    {asset?.mainPhotoUrl ? <img src={asset.mainPhotoUrl} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" /> : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400"><PackageCheck size={18} /></span>}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-ink">{asset?.patrimonyNumber ?? "Equipamento"}</span>
                      <span className="block truncate text-xs text-slate-500">{asset?.description ?? reservation.assetId}</span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1">
                      <StatusPill tone={reservation.status === "em_uso" ? "blue" : "yellow"}>{reservation.status === "em_uso" ? "Com você" : "Reservado"}</StatusPill>
                      {overdue ? <StatusPill tone="red">Atrasado</StatusPill> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
}
